import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Zap, MapPin, Mail, Phone, Camera, X, ArrowLeft, Loader2, CheckCircle2, ShieldCheck, Clock, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ClockTimePicker } from '../components/ClockTimePicker';
import { quickPostCreate, getCounties } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ITEM_TYPES = [
  { value: 'package', label: 'Package / Box' },
  { value: 'envelope', label: 'Envelope / Documents' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'sofa', label: 'Sofa / Couch' },
  { value: 'appliance', label: 'Appliance' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'pallet', label: 'Pallet' },
  { value: 'house_move', label: 'House Move' },
  { value: 'other', label: 'Other' },
];

const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 3 * 1024 * 1024; // 3 MB per photo
const TITLE_MIN = 4;
const TITLE_MAX = 80;
const DESC_MIN = 10;
const DESC_MAX = 500;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function QuickPostPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthedCustomer = !!(user && user.role === 'customer');
  // Route-aware "back": logged-in users return to their dashboard, guests
  // return to the public landing page. Avoids the white-screen flash that
  // happens when an authenticated user briefly hits "/".
  const backHref = user
    ? (user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/customer')
    : '/';
  const [form, setForm] = useState({
    title: '',
    description: '',
    pickup_location: '',
    pickup_county: '',
    dropoff_location: '',
    dropoff_county: '',
    email: isAuthedCustomer ? (user.email || '') : '',
    phone: isAuthedCustomer ? (user.phone || '') : '',
    receiver_email: '',
    item_type: '',
  });
  const [photos, setPhotos] = useState([]); // data URLs
  const [submitting, setSubmitting] = useState(false);
  const [counties, setCounties] = useState([]);

  // Prefill email/phone when the auth context resolves. The form's useState
  // initializer runs before AuthContext.checkAuth() finishes, so user is null
  // on the first render. This effect picks the values up the moment they're
  // available — only overwriting blank fields so it never clobbers
  // anything the user already typed.
  useEffect(() => {
    if (!isAuthedCustomer) return;
    setForm((f) => ({
      ...f,
      email: f.email || user.email || '',
      phone: f.phone || user.phone || '',
    }));
  }, [isAuthedCustomer, user]);
  // Delivery window — mirrors the detailed form's preferred_time JSON shape.
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTimeType, setDeliveryTimeType] = useState('before');
  const [deliveryTimeBefore, setDeliveryTimeBefore] = useState('');
  const [deliveryTimeFrom, setDeliveryTimeFrom] = useState('');
  const [deliveryTimeTo, setDeliveryTimeTo] = useState('');

  useEffect(() => {
    getCounties().then((res) => setCounties(res.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    const accepted = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only ${MAX_PHOTOS} photos allowed. Extra files were skipped.`);
    }
    const next = [...photos];
    for (const f of accepted) {
      if (!f.type.startsWith('image/')) {
        toast.error(`${f.name}: not an image file.`);
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name}: max 3 MB per photo.`);
        continue;
      }
      try {
        const url = await fileToDataUrl(f);
        next.push(url);
      } catch {
        toast.error(`Couldn't read ${f.name}`);
      }
    }
    setPhotos(next.slice(0, MAX_PHOTOS));
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((arr) => arr.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const t = form.title.trim();
    const d = form.description.trim();
    if (t.length < TITLE_MIN) return `Title must be at least ${TITLE_MIN} characters.`;
    if (t.length > TITLE_MAX) return `Title must be ${TITLE_MAX} characters or fewer.`;
    if (d.length < DESC_MIN) return `Description must be at least ${DESC_MIN} characters.`;
    if (d.length > DESC_MAX) return `Description must be ${DESC_MAX} characters or fewer.`;
    if (!form.pickup_location.trim()) return 'Pickup location is required.';
    if (!form.pickup_county) return 'Please select the pickup county.';
    if (!form.dropoff_location.trim()) return 'Delivery location is required.';
    if (!form.dropoff_county) return 'Please select the delivery county.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    if (form.phone.trim().length < 6) return 'Please enter a valid mobile number.';
    if (form.receiver_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.receiver_email.trim())) return 'Please enter a valid receiver email address.';
    if (!deliveryDate) return 'Please choose a delivery date.';
    if (deliveryTimeType === 'before' && !deliveryTimeBefore) return 'Please choose the delivery cut-off time.';
    if (deliveryTimeType === 'range' && (!deliveryTimeFrom || !deliveryTimeTo)) return 'Please choose the delivery time range.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const to24h = (val) => {
        if (!val) return '';
        const [h, m, ap] = val.split(':');
        let hr = parseInt(h, 10);
        if (ap === 'PM' && hr !== 12) hr += 12;
        if (ap === 'AM' && hr === 12) hr = 0;
        return `${String(hr).padStart(2, '0')}:${m}`;
      };
      const timeData = { type: deliveryTimeType, date: deliveryDate };
      if (deliveryTimeType === 'before') timeData.before = to24h(deliveryTimeBefore);
      else if (deliveryTimeType === 'range') {
        timeData.from = to24h(deliveryTimeFrom);
        timeData.to = to24h(deliveryTimeTo);
      }

      const res = await quickPostCreate({
        title: form.title.trim(),
        description: form.description.trim(),
        pickup_location: form.pickup_location.trim(),
        pickup_county: form.pickup_county,
        dropoff_location: form.dropoff_location.trim(),
        dropoff_county: form.dropoff_county,
        email: form.email.trim(),
        phone: form.phone.trim(),
        receiver_email: form.receiver_email.trim() || undefined,
        item_type: form.item_type || undefined,
        photos,
        preferred_time: JSON.stringify(timeData),
      });
      // Logged-in customer flow: backend skips email verification and
      // publishes immediately. Send them straight to their dashboard.
      if (user && user.role === 'customer' && res.data?.status === 'pending') {
        toast.success('Your delivery request is live!');
        navigate('/customer?view=jobs', { replace: true });
        return;
      }
      toast.success('Almost done — check your email to publish!');
      navigate(`/quick-post/status/${res.data.guest_token}?status=pending_verification`, { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to post delivery. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="relative max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to={backHref} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium" data-testid="quick-post-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <Link to="/" aria-label="JustDeliver home" data-testid="quick-post-header-logo" className="absolute left-1/2 -translate-x-1/2">
            <img src="/logo.png" alt="JustDeliver" className="h-7 sm:h-8 w-auto" />
          </Link>
          <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-bold">
            <Zap className="w-3.5 h-3.5" fill="currentColor" /> QUICK POST
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white shadow-lg shadow-emerald-100 ring-1 ring-emerald-100 mb-5 p-2.5" data-testid="quick-post-jd-icon">
            <img src="/jd-icon.png" alt="JustDeliver" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
            {isAuthedCustomer ? 'Quick Post a delivery' : 'Post a delivery in under 30 seconds'}
          </h1>
          <p className="mt-3 text-base text-gray-600 max-w-xl mx-auto">
            {isAuthedCustomer
              ? <>Fastest way to post — your delivery goes live the moment you submit. Drivers will start bidding on <span className="font-semibold text-[#1B5E20]">JustDeliver.ie</span>.</>
              : <>No account needed to start. Just verify your email and drivers will start bidding on <span className="font-semibold text-[#1B5E20]">JustDeliver.ie</span>.</>}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl shadow-amber-100/40 border border-amber-100 p-6 sm:p-8 space-y-5"
          data-testid="quick-post-form"
        >
          {/* Title */}
          <div>
            <Label htmlFor="qp-title" className="text-sm font-semibold text-gray-800">
              Delivery title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="qp-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={TITLE_MAX}
              placeholder="e.g. Sofa from Dublin to Galway"
              className="mt-1.5"
              data-testid="quick-post-title"
            />
            <p className="mt-1 text-xs text-gray-500">{form.title.length}/{TITLE_MAX} characters</p>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="qp-desc" className="text-sm font-semibold text-gray-800">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="qp-desc"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              maxLength={DESC_MAX}
              rows={3}
              placeholder="Describe the item — size, weight, anything drivers should know. Do not share phone numbers or emails here."
              className="mt-1.5 resize-none"
              data-testid="quick-post-description"
            />
            <p className="mt-1 text-xs text-gray-500">
              {form.description.length}/{DESC_MAX} characters (min {DESC_MIN})
            </p>
          </div>

          {/* Item type — same vocabulary as the detailed form */}
          <div>
            <Label htmlFor="qp-item-type" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-gray-600" /> Item type <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <select
              id="qp-item-type"
              value={form.item_type}
              onChange={(e) => set('item_type', e.target.value)}
              className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white h-10"
              data-testid="quick-post-item-type"
            >
              <option value="">Select item type…</option>
              {ITEM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Helps drivers spot the job that matches their vehicle.
            </p>
          </div>

          {/* Pickup + Dropoff */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="qp-pickup" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-green-600" /> Pickup location <span className="text-red-500">*</span>
              </Label>
              <Input
                id="qp-pickup"
                value={form.pickup_location}
                onChange={(e) => set('pickup_location', e.target.value)}
                placeholder="Address, town or eircode"
                data-testid="quick-post-pickup"
              />
              <select
                value={form.pickup_county}
                onChange={(e) => set('pickup_county', e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="quick-post-pickup-county"
              >
                <option value="">Select pickup county…</option>
                {counties.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qp-dropoff" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-orange-600" /> Delivery location <span className="text-red-500">*</span>
              </Label>
              <Input
                id="qp-dropoff"
                value={form.dropoff_location}
                onChange={(e) => set('dropoff_location', e.target.value)}
                placeholder="Address, town or eircode"
                data-testid="quick-post-dropoff"
              />
              <select
                value={form.dropoff_county}
                onChange={(e) => set('dropoff_county', e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="quick-post-dropoff-county"
              >
                <option value="">Select delivery county…</option>
                {counties.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Email + Phone */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="qp-email" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-gray-600" /> Email address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="qp-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                data-testid="quick-post-email"
              />
            </div>
            <div>
              <Label htmlFor="qp-phone" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-gray-600" /> Mobile number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="qp-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+353 85 123 4567"
                className="mt-1.5"
                data-testid="quick-post-phone"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 flex items-start gap-1.5 bg-gray-50 rounded-md p-3">
            <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            Your contact details stay private. Drivers only see them after you accept a bid.
          </p>

          {/* Receiver email — optional; the 6-digit delivery code goes here */}
          <div>
            <Label htmlFor="qp-receiver-email" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-gray-600" /> Receiver&apos;s email <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="qp-receiver-email"
              type="email"
              value={form.receiver_email}
              onChange={(e) => set('receiver_email', e.target.value)}
              placeholder="receiver@example.com"
              className="mt-1.5"
              data-testid="quick-post-receiver-email"
            />
            <p className="mt-1 text-xs text-gray-500">
              We&apos;ll email the 6-digit delivery code here once you accept a driver — the receiver shares it on arrival to confirm completion. Leave blank to receive it yourself.
            </p>
          </div>

          {/* Delivery window — same format as the detailed form */}
          <div>
            <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5 text-gray-600" /> When do you need it delivered? <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Date</Label>
                <Input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="rounded-md"
                  data-testid="quick-post-delivery-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Timing</Label>
                <select
                  value={deliveryTimeType}
                  onChange={(e) => setDeliveryTimeType(e.target.value)}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white h-10"
                  data-testid="quick-post-delivery-time-type"
                >
                  <option value="before">Before a specific time</option>
                  <option value="range">Time range</option>
                  <option value="flexible">Flexible / Any time</option>
                </select>
              </div>
            </div>
            {deliveryTimeType === 'before' && (
              <div className="mt-2 space-y-1">
                <Label className="text-[11px] text-gray-500">Deliver before</Label>
                <ClockTimePicker value={deliveryTimeBefore} onChange={setDeliveryTimeBefore} label="deliver before" />
              </div>
            )}
            {deliveryTimeType === 'range' && (
              <div className="mt-2 flex items-end gap-3 flex-wrap">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">From</Label>
                  <ClockTimePicker value={deliveryTimeFrom} onChange={setDeliveryTimeFrom} label="from" />
                </div>
                <span className="text-gray-400 text-sm pb-2">to</span>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">To</Label>
                  <ClockTimePicker value={deliveryTimeTo} onChange={setDeliveryTimeTo} label="to" />
                </div>
              </div>
            )}
            {deliveryTimeType === 'flexible' && (
              <p className="mt-2 text-xs text-gray-400 italic">Driver can deliver any time on the selected date.</p>
            )}
          </div>

          {/* Photos */}
          <div>
            <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-gray-600" /> Photos <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <p className="text-xs text-gray-500 mt-1 mb-2">
              Add up to {MAX_PHOTOS} photos to receive more accurate bids from drivers.
            </p>
            <div className="flex flex-wrap gap-3">
              {photos.map((src, i) => (
                <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200" data-testid={`quick-post-photo-${i}`}>
                  <img src={src} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                    data-testid={`quick-post-remove-photo-${i}`}
                    aria-label="Remove photo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label
                  className="w-24 h-24 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 flex flex-col items-center justify-center cursor-pointer text-amber-700 text-xs font-medium"
                  data-testid="quick-post-add-photo"
                >
                  <Camera className="w-5 h-5 mb-1" />
                  Add photo
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onPhotoSelect}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-200"
            data-testid="quick-post-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</>
            ) : (
              <><Zap className="w-4 h-4 mr-2" fill="currentColor" /> Post Delivery</>
            )}
          </Button>

          {isAuthedCustomer ? (
            <p className="text-xs text-gray-500 flex items-start gap-2 pt-2" data-testid="quick-post-helper-authed">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              Your delivery goes live instantly — no verification needed.
            </p>
          ) : (
            <ul className="text-xs text-gray-500 space-y-1.5 pt-2" data-testid="quick-post-helper-guest">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Free to post. Pay only when you accept a driver.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> No account needed — just verify your email to go live.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Already have an account? <Link to="/login" className="text-green-700 font-semibold underline">Sign in</Link>.</li>
            </ul>
          )}
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          Need more options like dimensions, multi-drop, or photos per stop?{' '}
          <Link to="/register" className="text-green-700 font-semibold underline" data-testid="quick-post-detailed-link">
            Use the detailed delivery form
          </Link>
        </p>
      </main>
    </div>
  );
}
